import { memo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertReviewStrip, { type StripItem } from "./AlertReviewStrip";
import { X, Pin, PinOff, Plus, Maximize2 } from "lucide-react";
import VideoPreviewEnhanced, {
  type VideoOrientation,
} from "@/machines/components/common/timeline-editor/video-preview/video-preview-enhanced";
import SecondaryCamera from "@/machines/components/common/timeline-editor/video-preview/secondary-camera";
import { useMultiCameraStore } from "@/shared/stores/useMultiCameraStore";
import { useModal } from "@/shared/lib/modal/modal-context";
import { cn } from "@/ui/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { useMapPinStore } from "@/map/stores/useMapPinStore";
import {
  clampPanelSize,
  useAlertReviewLayoutStore,
} from "@/map/stores/useAlertReviewLayoutStore";
import FloatingWindow from "./FloatingWindow";

/** Minimal machine shape needed to stream + label one video tile. */
interface VideoStackMachine {
  id: number;
  name: string;
  device_mxid: string;
  color?: string;
}

interface MapVideoPanelProps {
  deviceMxid: string;
  machineName: string;
  machineColor?: string;
  /** Needed to add/track extra camera angles for this machine. */
  machineId: number;
  locationId?: number;
  /** Every machine at the location — one video tile is streamed per machine. */
  machines: VideoStackMachine[];
  /** The active machine drives the shared cursor; the rest are seek-only followers. */
  activeMachineId: number | null;
  /**
   * Map live-follow armed (alert-review + realtime + live). Keeps the PRIMARY
   * tile playing muted so it autoplays without a "Go live" click. Off elsewhere.
   */
  liveAutoplay?: boolean;
  /**
   * Reports the PRIMARY tile's newest footage edge up to the page (so the shared
   * cursor can be pinned to real footage while live). Followers never report.
   */
  onFootageEdgeChange?: (edge: { startMs: number; endMs: number } | null) => void;
  /**
   * "Go live" jump target (ms), set anew on each Go-live click. Routed to the
   * PRIMARY tile so it resets to the live edge instead of crawling from the alert.
   */
  jumpToLiveTime?: number | null;
  /** Floating (draggable) vs docked to the right edge. */
  floating: boolean;
  /** Docked side. In review mode the video docks as a fixed LEFT column. */
  dockSide?: "left" | "right";
  /**
   * Alert Review V2: the docked column becomes the review's LEFT PANEL — the
   * same size as the alert frame opposite it, leaving the map corridor between
   * them. Sized from the shared layout store rather than by prop so both sides
   * cannot drift apart; see `useAlertReviewLayoutStore`.
   */
  reviewLayout?: boolean;
  /**
   * Alert review keeps the docked panel permanently mounted, so its close
   * control is hidden rather than left on screen as a dead button.
   */
  hideClose?: boolean;
  onToggleFloat: () => void;
  onClose: () => void;
}

/**
 * One machine's video, synced to the ONE shared timeline cursor. The active
 * machine's tile is the PRIMARY (`followerMode` off): its playback drives the
 * shared cursor. Every other tile is a follower — it mirrors the primary's
 * play/pause/rate and NEVER writes the cursor from playback (exactly one
 * writer, so no write-back war). Source is per-device (each tile fetches ITS
 * machine's footage via `device_mxid`) while the seek target is the shared
 * cursor read from the surrounding TimelineContext.
 *
 * Every tile shows the SAME controls bar (`sharedTransport`), because the bar is
 * a remote for the one stack rather than a per-tile player: pressing play or
 * scrubbing on a follower mutates the shared playback state, the primary acts on
 * it, and the mirror effects carry every tile. Fullscreen stays per-tile.
 */
const MachineVideoTile = memo(({
  deviceMxid,
  machineName,
  machineColor,
  isPrimary,
  liveAutoplay = false,
  liveFollowing = false,
  onFootageEdgeChange,
  jumpToLiveTime,
  initialOrientation = "vertical",
  fill = false,
}: {
  deviceMxid: string;
  machineName: string;
  machineColor?: string;
  isPrimary: boolean;
  liveAutoplay?: boolean;
  liveFollowing?: boolean;
  onFootageEdgeChange?: (edge: { startMs: number; endMs: number } | null) => void;
  jumpToLiveTime?: number | null;
  /** The review panel is landscape; the docked column is not. */
  initialOrientation?: VideoOrientation;
  /** Fill the parent instead of flexing against sibling tiles. */
  fill?: boolean;
}) => {
  const { t } = useTranslation("map");
  const [orientation, setOrientation] = useState<VideoOrientation>(initialOrientation);
  return (
    <div
      // Active (controllable) tile floats to the bottom of the panel so its
      // control bar is always at the bottom. CSS `order` only (as a class, so a
      // re-render doesn't allocate a new style object) — the DOM node never
      // moves, so the playing <video> is never reset on a switch.
      className={cn(
        isPrimary ? "order-1" : "order-none",
        "flex flex-col overflow-hidden rounded-lg border border-border",
        // Filling the review panel: no min-height fighting the panel's own size,
        // so the frame letterboxes and the control bar always has its row.
        fill ? "h-full w-full" : "min-h-[220px] flex-1",
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-2 py-1 text-xs">
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: machineColor || "#EF4444" }}
        />
        <span className="truncate font-medium">{machineName}</span>
        {isPrimary && (
          <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            {t("video.active")}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 bg-black/95">
        <VideoPreviewEnhanced
          device_mxid={deviceMxid}
          orientation={orientation}
          // Only the primary shows the orientation toggle (its full toolbar);
          // followers hide all controls, so leave it undefined for them.
          onOrientationChange={isPrimary ? setOrientation : undefined}
          followerMode={!isPrimary}
          liveAutoplay={liveAutoplay}
          liveFollowing={liveFollowing}
          // Only the primary reports footage; followers mirror the primary.
          onFootageEdgeChange={isPrimary ? onFootageEdgeChange : undefined}
          jumpToLiveTime={isPrimary ? jumpToLiveTime : null}
          // Map: strip the icon toolbar to just fullscreen (keep the playback row).
          minimalControls
          // Every tile in the stack carries the same bar. On a follower it is a
          // second REMOTE for the one shared playback (play/pause, scrub and
          // speed go through the shared state, so both tiles move together);
          // fullscreen stays per-tile. Inert on the primary, which already owns
          // playback and keeps its local handlers.
          sharedTransport
        />
      </div>
    </div>
  );
});

/**
 * Timeline-synced video for `/map`. Docks in the right "Video" drawer, as a
 * fixed LEFT column (review mode), or pops out into a draggable floating window.
 * Renders ONE `VideoPreviewEnhanced` per machine at the location, stacked and
 * scrollable — all streaming simultaneously and all synced to the single shared
 * timeline cursor (read from the surrounding TimelineContext). The active
 * machine's tile is the primary/controllable one; the rest are followers.
 * Layout: one machine → full height, two → ~50/50, N → scroll for the overflow.
 * Extra camera angles (added via the modal) stack below the primary machine.
 */
const MapVideoPanel = ({
  deviceMxid,
  machineName,
  machineColor,
  machineId,
  locationId,
  machines,
  activeMachineId,
  liveAutoplay = false,
  onFootageEdgeChange,
  jumpToLiveTime,
  floating,
  dockSide = "right",
  reviewLayout = false,
  hideClose = false,
  onToggleFloat,
  onClose,
}: MapVideoPanelProps) => {
  const { t } = useTranslation("map");
  const { openModal } = useModal();
  const { getCameras } = useMultiCameraStore();
  const storedCameras = getCameras(machineId);

  // This panel already renders a tile for EVERY machine at the location, so an
  // "extra angle" pointing at one of them is that machine shown twice. The angle
  // picker offers other machines' devices, which on the map can only ever
  // produce that duplicate — a two-machine location showing three videos.
  //
  // Filtered rather than deleted from the store: machine-detail shows ONE
  // machine, where adding a neighbouring camera is exactly the point, and it
  // reads the same store. Dropping the entry here would take it away there too.
  const shownDevices = new Set(machines.map((m) => m.device_mxid));
  const extraCameras = storedCameras.filter((mxid) => !shownDevices.has(mxid));

  // ...and for the same reason there is nothing left to offer: every candidate
  // is already on screen. This leaves the control for surfaces where the machine
  // list is not the whole location.
  const hasOtherAngle = machines.some(
    (m) =>
      m.device_mxid !== deviceMxid &&
      !storedCameras.includes(m.device_mxid) &&
      !shownDevices.has(m.device_mxid),
  );

  // Layout pin (persisted, shared with the other map panels). Freezing the
  // video's dock side is done at the page level (so its neighbours stay in
  // sync); this button drives that pin. Playback is unaffected — the videos
  // keep streaming while pinned. The pop-out toggle uses a distinct Maximize2
  // icon so the two controls don't both read as "Pin".
  const reviewPanelSize = useAlertReviewLayoutStore((s) => s.videoSize);
  const reviewAlertWidth = useAlertReviewLayoutStore((s) => s.alertSize.width);
  const setReviewPanelSize = useAlertReviewLayoutStore((s) => s.setPanelSize);
  const reviewDragFrom = useRef<{ x: number; y: number } | null>(null);
  const reviewShellRef = useRef<HTMLDivElement>(null);
  /**
   * Which camera the review shows. Every tile stays MOUNTED and only the chosen
   * one is visible: the stack's own rule is that a playing `<video>` node is
   * never moved or remounted, so switching by unmounting would drop the buffer
   * and restart the stream each time the operator glanced at another machine.
   */
  const [reviewCameraIndex, setReviewCameraIndex] = useState(0);
  const reviewCameras: StripItem[] = machines.map((m) => ({
    key: m.device_mxid,
    src: null,
    label: m.name,
  }));
  const activeReviewIndex = Math.min(reviewCameraIndex, Math.max(0, machines.length - 1));

  /** Grow/shrink the camera against whatever the alert frame opposite takes. */
  const resizeReview = (dx: number, dy: number) => {
    const stageEl = reviewShellRef.current?.parentElement;
    if (!stageEl) return;
    setReviewPanelSize(
      "video",
      clampPanelSize(
        { width: reviewPanelSize.width + dx, height: reviewPanelSize.height + dy },
        { width: stageEl.clientWidth - 40, height: stageEl.clientHeight - 250 },
        reviewAlertWidth,
      ),
    );
  };
  const isPinned = useMapPinStore((s) => s.isPinned("video"));
  const togglePin = useMapPinStore((s) => s.togglePin);

  const title = (
    <>
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: machineColor || "#EF4444" }}
      />
      <span className="truncate font-medium">{machineName}</span>
      <span className="shrink-0 text-muted-foreground">{t("video.titleSuffix")}</span>
    </>
  );

  const content = (
    <div className="flex h-full flex-col gap-2 overflow-y-auto">
      {locationId != null && hasOtherAngle && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full shrink-0 gap-2 text-muted-foreground"
          onClick={() =>
            openModal("add-camera-angle", {
              machineId,
              locationId,
              currentDeviceMxid: deviceMxid,
            })
          }
        >
          <Plus className="h-3.5 w-3.5" />
          {t("video.addAngle")}
        </Button>
      )}

      {/* One video per machine — all streaming at once, all synced to the one
          shared cursor. Keyed by device so an active-machine switch only flips
          each tile's primary/follower role (no remount, buffered frames kept).
          DOM order stays fixed (never move a playing <video> node); the active
          tile is floated to the bottom purely via CSS `order` so its control
          bar always sits at the bottom of the panel. */}
      {machines.map((m) => (
        <MachineVideoTile
          key={m.device_mxid}
          deviceMxid={m.device_mxid}
          machineName={m.name}
          machineColor={m.color}
          isPrimary={m.id === activeMachineId}
          // Only the primary (active) tile keep-alive-plays while live; followers
          // stay on their mirror-the-primary path.
          liveAutoplay={liveAutoplay && m.id === activeMachineId}
          // Panel-level: every tile learns the view is live, so a follower can
          // roll to its next clip instead of waiting to be dragged there.
          liveFollowing={liveAutoplay}
          onFootageEdgeChange={m.id === activeMachineId ? onFootageEdgeChange : undefined}
          jumpToLiveTime={m.id === activeMachineId ? jumpToLiveTime : null}
        />
      ))}

      {extraCameras.map((extraMxid) => (
        <SecondaryCamera key={extraMxid} device_mxid={extraMxid} machineId={machineId} />
      ))}
    </div>
  );

  if (floating) {
    return (
      <FloatingWindow title={title} width={320} bodyClassName="h-[360px] p-2" onDock={onToggleFloat} onClose={onClose}>
        {content}
      </FloatingWindow>
    );
  }

  if (reviewLayout) {
    return (
      <div
        ref={reviewShellRef}
        className={cn(
          "absolute left-5 top-16 z-20 flex flex-col overflow-hidden rounded-lg",
          "border border-border bg-background shadow-lg animate-in duration-200 slide-in-from-left",
        )}
        style={{ width: reviewPanelSize.width, height: reviewPanelSize.height }}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1.5">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            {machines[activeReviewIndex]?.name ?? machineName}
            <span className="shrink-0 text-muted-foreground">{t("video.cameraSuffix")}</span>
          </div>
        </header>
        <div className="flex min-h-0 flex-1 gap-2 p-2">
          {machines.length > 1 && (
            <AlertReviewStrip
              items={reviewCameras}
              activeIndex={activeReviewIndex}
              onSelect={setReviewCameraIndex}
              label={t("video.cameras")}
              side="left"
              className="w-20"
            />
          )}
          <div className="relative min-w-0 flex-1">
            {machines.map((m, i) => (
              <div
                key={m.device_mxid}
                className={cn("absolute inset-0", i === activeReviewIndex ? "" : "invisible")}
                aria-hidden={i !== activeReviewIndex}
              >
                <MachineVideoTile
                  deviceMxid={m.device_mxid}
                  machineName={m.name}
                  machineColor={m.color}
                  fill
                  initialOrientation="horizontal"
                  isPrimary={m.id === activeMachineId}
                  liveAutoplay={liveAutoplay && m.id === activeMachineId}
                  liveFollowing={liveAutoplay}
                  onFootageEdgeChange={m.id === activeMachineId ? onFootageEdgeChange : undefined}
                  jumpToLiveTime={m.id === activeMachineId ? jumpToLiveTime : null}
                />
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          aria-label={t("video.resizePanel")}
          title={t("video.dragToResize")}
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            reviewDragFrom.current = { x: e.clientX, y: e.clientY };
          }}
          onPointerMove={(e) => {
            if (!reviewDragFrom.current) return;
            resizeReview(e.clientX - reviewDragFrom.current.x, e.clientY - reviewDragFrom.current.y);
            reviewDragFrom.current = { x: e.clientX, y: e.clientY };
          }}
          onPointerUp={() => {
            reviewDragFrom.current = null;
          }}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 64 : 16;
            if (e.key === "ArrowRight") resizeReview(step, 0);
            else if (e.key === "ArrowLeft") resizeReview(-step, 0);
            else if (e.key === "ArrowDown") resizeReview(0, step);
            else if (e.key === "ArrowUp") resizeReview(0, -step);
            else return;
            e.preventDefault();
            e.stopPropagation();
          }}
          className={cn(
            "absolute bottom-2 right-2 flex h-6 w-6 cursor-nwse-resize items-center justify-center",
            "rounded-md border border-border bg-background/80 text-muted-foreground",
            "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true">
            <path d="M1 11 11 1M1 7v4h4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "absolute top-0 bottom-0 z-20 flex w-[340px] flex-col border-border bg-background shadow-xl animate-in duration-200",
        dockSide === "left"
          ? "left-0 border-r slide-in-from-left"
          : "right-0 border-l slide-in-from-right",
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-2 text-sm">{title}</div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => togglePin("video")}
            aria-label={isPinned ? t("video.unpin") : t("video.pin")}
            title={isPinned ? t("video.unpin") : t("video.pin")}
            className={cn(
              "rounded-md p-1 hover:bg-muted hover:text-foreground",
              isPinned ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={onToggleFloat}
            aria-label={t("video.popOutAria")}
            title={t("video.popOut")}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          {!hideClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("video.close")}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 p-2">{content}</div>
    </div>
  );
};

export default MapVideoPanel;
